import { useEffect, useEffectEvent, useId, useState, type KeyboardEvent, type Ref } from "react";

import { useCustomerSearch, type CustomerOut } from "@/api";
import fields from "@/components/fields/Field.module.css";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

import styles from "./CustomerSearch.module.css";

type Option = { kind: "customer"; customer: CustomerOut } | { kind: "new" };

/** Only my own Vorgänge are ever counted (D-1, contract §1.3 `my_case_count`). */
function ownCases(count: number): string {
  if (count === 0) return "keine eigenen Vorgänge";
  return count === 1 ? "1 eigener Vorgang" : `${count} eigene Vorgänge`;
}

interface CustomerSearchProps {
  label: string;
  /** What stands in the field — the owner keeps it, so it survives the form the field gives way to. */
  query: string;
  onQueryChange: (query: string) => void;
  /** A customer of the master was taken from the list. */
  onPick: (customer: CustomerOut) => void;
  /** „Neu anlegen: „…"" under the found ones once something is typed; left out, it is not offered. */
  onCreate?: (text: string) => void;
  /** Escape with the list already closed — the way out of whatever holds the field. */
  onEscape?: () => void;
  /** A pick is being written: the field stays, read-only, and says it is busy. */
  busy?: boolean;
  /** Nothing more may be picked (the Vorgang is gone): read-only, the list stays. */
  held?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}

/**
 * The shared customer master as a search field (design 2026-09-22 §8.2,
 * D-3): one field, the found customers under it. The list follows the ARIA
 * combobox pattern — arrows move, Enter takes, Escape closes and a second
 * Escape leaves; both Escapes are marked handled, so a dialog around does not
 * close on the first one.
 *
 * Taken out of the record's `CustomerField` (spec 2026-10-20, Task 10) so the
 * Eingang's „Anderen Kunden suchen" is the same search, not a second one:
 * the record assigns what is picked, the Eingang saves the contact person
 * with it. What a pick does is the owner's.
 *
 * While a new term waits for its search the list says so (a status line and
 * `aria-busy`) — and nothing dims it while it waits: faded text fell to
 * 2.2:1 (record fix round 2, I-4).
 */
export function CustomerSearch({
  label,
  query,
  onQueryChange,
  onPick,
  onCreate,
  onEscape,
  busy = false,
  held = false,
  inputRef,
}: CustomerSearchProps) {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(-1);
  // An Enter pressed while the search still ran, kept until it answers.
  const [waiting, setWaiting] = useState(false);
  const term = useDebouncedValue(query, 250);
  const search = useCustomerSearch(term);
  const found = search.data ?? [];
  // A term still waiting for its search, or an older list standing in while
  // the new one loads — either way, what is listed is not yet the answer.
  const searching =
    query.trim() !== term.trim() ||
    (search.isFetching === true && (search.isPlaceholderData === true || search.data === undefined));
  const options: Option[] = [
    ...found.map((customer): Option => ({ kind: "customer", customer })),
    ...(onCreate !== undefined && query.trim() !== "" ? [{ kind: "new" } as const] : []),
  ];

  function choose(option: Option) {
    if (busy || held) return;
    if (option.kind === "customer") {
      onPick(option.customer);
      return;
    }
    setOpen(false);
    onCreate?.(query.trim());
  }

  /**
   * Enter with nothing under the cursor, once the list is the answer. Nobody
   * found: the form, which writes nothing yet. Somebody found: the first is
   * put under the cursor, not assigned — the next Enter takes them.
   */
  function enter() {
    if (options.length === 0) return;
    if (found.length === 0) choose(options[0]!);
    else setActive(0);
  }

  const settleWaitingEnter = useEffectEvent(() => {
    setWaiting(false);
    enter();
  });
  useEffect(() => {
    if (!waiting || searching) return undefined;
    // From a timer, so no state is set synchronously in the effect.
    const timer = window.setTimeout(() => settleWaitingEnter(), 0);
    return () => window.clearTimeout(timer);
  }, [waiting, searching]);

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setWaiting(false);
      setOpen(true);
      setActive((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setWaiting(false);
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!open) return;
      const option = options[active];
      if (option !== undefined) {
        choose(option);
        return;
      }
      // Nothing under the cursor — typing leaves nothing there (live
      // 27.10.2026: „Kaiser", Enter, and nothing happened). While the list
      // may still change the Enter waits for it instead of being dropped
      // (live 28.10.2026: „Kaiser" typed straight into Enter, nothing again).
      if (searching) setWaiting(true);
      else enter();
    } else if (event.key === "Escape") {
      // Handled here, either way — the dialog around checks `defaultPrevented`.
      event.preventDefault();
      setWaiting(false);
      if (open && options.length > 0) setOpen(false);
      else onEscape?.();
    }
  }

  const listed = open && options.length > 0;

  return (
    <>
      <div className={fields.field}>
        <div className={styles.head}>
          <label className={`label ${fields.fieldLabel}`} htmlFor={`${id}-input`}>
            {label}
          </label>
          {/* Present before its text, so the change is read (F-13). */}
          <span role="status" className={styles.status}>
            {searching ? "Suche läuft …" : ""}
          </span>
        </div>
        <input
          ref={inputRef}
          id={`${id}-input`}
          role="combobox"
          className={fields.input}
          aria-expanded={listed}
          aria-controls={listed ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={listed && active >= 0 && active < options.length ? `${id}-option-${active}` : undefined}
          autoComplete="off"
          data-autofocus
          placeholder="Name, Firma, E-Mail oder Kundennummer"
          value={query}
          // Read-only, not disabled, while a customer is being assigned:
          // a disabled field throws its focus to <body> (record fix wave I1).
          readOnly={busy || held}
          aria-busy={busy || undefined}
          onChange={(event) => {
            onQueryChange(event.target.value);
            setActive(-1);
            setWaiting(false);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {listed ? (
        <ul id={listId} role="listbox" aria-label="Kunden" aria-busy={searching || undefined} className={styles.options}>
          {options.map((option, index) => (
            <li
              key={option.kind === "customer" ? option.customer.id : "new"}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === active}
              className={styles.option}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              {option.kind === "customer" ? (
                <>
                  <span className={styles.name}>{option.customer.company ?? option.customer.displayName}</span>
                  <span className={styles.sub}>
                    {[
                      option.customer.company !== null ? option.customer.displayName : null,
                      option.customer.email,
                      ownCases(option.customer.myCaseCount),
                    ]
                      .filter((part): part is string => part !== null && part !== "")
                      .join(" · ")}
                  </span>
                </>
              ) : (
                <span>Neu anlegen: „{query.trim()}“</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
